import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "uploadZone", "fileList", "fileTemplate"]
  static values = {
    maxSize: { type: Number, default: 5242880 }, // 5MB default
    maxFiles: { type: Number, default: 10 },
    allowedTypes: { type: Array, default: ["image/png", "image/jpeg"] }
  }

  connect() {
    this.pendingFiles = []
    this.submitBlocked = false
    this.element.addEventListener("submit", this.preventSubmitOnError)
  }

  disconnect() {
    this.element.removeEventListener("submit", this.preventSubmitOnError)
  }

  preventSubmitOnError = (event) => {
    if (this.submitBlocked) {
      event.preventDefault()
      this.inputTarget.setCustomValidity("Please fix file errors before submitting")
      this.inputTarget.reportValidity()
    }
  }

  handlePaste(event) {
    const items = event.clipboardData?.items
    if (!items) return

    const files = []
    for (let item of items) {
      if (item.kind === "file") {
        files.push(item.getAsFile())
      }
    }

    if (files.length > 0) {
      event.preventDefault()
      this.addFiles(files)
    }
  }

  validateFile(file) {
    if (!file) return { valid: false, reason: "No file provided" }
    if (!this.allowedTypesValue.includes(file.type)) {
      return { valid: false, reason: "File type not allowed (PNG or JPG only)" }
    }
    if (file.size > this.maxSizeValue) {
      const maxMB = Math.round(this.maxSizeValue / 1048576)
      return { valid: false, reason: `File too large (max ${maxMB}MB)` }
    }
    return { valid: true }
  }

  isDuplicate(file) {
    return this.pendingFiles.some(f =>
      f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
    )
  }

  fileSelected() {
    const files = Array.from(this.inputTarget.files)
    this.inputTarget.value = ""
    this.addFiles(files)
  }

  addFiles(files) {
    const errors = []
    const newFiles = []

    for (const file of files) {
      if (this.isDuplicate(file)) {
        errors.push({ name: file.name, reason: "File already exists" })
        continue
      }
      const result = this.validateFile(file)
      if (!result.valid) {
        errors.push({ name: file.name, reason: result.reason })
        continue
      }
      newFiles.push(file)
    }

    const remaining = this.maxFilesValue - this.pendingFiles.length
    if (newFiles.length > remaining) {
      const rejected = newFiles.splice(remaining)
      for (const file of rejected) {
        errors.push({ name: file.name, reason: `Too many files (max ${this.maxFilesValue})` })
      }
    }

    if (newFiles.length > 0) {
      this.pendingFiles.push(...newFiles)
    }

    this.renderFiles()
    this.renderErrors(errors)
    this.submitBlocked = errors.length > 0 && this.pendingFiles.length === 0
    this.inputTarget.setCustomValidity("")
    this.syncInput()
  }

  removeFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.pendingFiles.splice(index, 1)
    this.renderFiles()
    this.syncInput()
  }

  dismissError(event) {
    event.preventDefault()
    event.currentTarget.closest('[data-role="fileRow"]')?.remove()
    const hasErrors = this.fileListTarget.querySelector("[data-error]") !== null
    if (!hasErrors) {
      this.submitBlocked = false
      this.inputTarget.setCustomValidity("")
    }
  }

  renderFiles() {
    this.fileListTarget.querySelectorAll('[data-role="fileRow"]:not([data-error])')
      .forEach(row => row.remove())

    const firstError = this.fileListTarget.querySelector("[data-error]")
    this.pendingFiles.forEach((file, index) => {
      const row = this.fileTemplateTarget.children[0].cloneNode(true)
      row.querySelector('[data-role="filename"]').textContent = file.name
      const removeBtn = row.querySelector('[data-action*="removeFile"]')
      if (removeBtn) removeBtn.dataset.index = index
      this.fileListTarget.insertBefore(row, firstError)
    })
  }

  renderErrors(errors) {
    for (const error of errors) {
      const row = this.fileTemplateTarget.children[0].cloneNode(true)
      const fileRow = row.querySelector('[data-role="fileRow"]') || row
      fileRow.dataset.error = "true"
      fileRow.classList.remove("border-gray-300")
      fileRow.classList.add("border-red-500", "bg-red-50", "dark:bg-red-950")

      row.querySelector('[data-role="filename"]').textContent = error.name

      const status = row.querySelector('[data-role="status"]')
      if (status) {
        status.classList.remove("hidden")
        status.classList.add("flex")
        const statusText = status.querySelector('[data-role="statusText"]')
        if (statusText) statusText.textContent = error.reason
      }

      const actions = row.querySelector('[data-role="actions"]')
      if (actions) actions.classList.add("hidden")

      const dismiss = row.querySelector('[data-role="dismiss"]')
      if (dismiss) dismiss.classList.remove("hidden")

      this.fileListTarget.appendChild(row)
    }
  }

  syncInput() {
    const dataTransfer = new DataTransfer()
    for (const file of this.pendingFiles) {
      dataTransfer.items.add(file)
    }
    this.inputTarget.files = dataTransfer.files
  }

  toggleDragStyling(active) {
    this.uploadZoneTarget.classList.toggle("border-gray-300", !active)
    this.uploadZoneTarget.classList.toggle("border-solid", active)
    this.uploadZoneTarget.classList.toggle("border-blue-500", active)
  }

  dragOver(event) {
    event.preventDefault()
    this.toggleDragStyling(true)
  }

  dragLeave(event) {
    event.preventDefault()
    this.toggleDragStyling(false)
  }

  drop(event) {
    event.preventDefault()
    this.toggleDragStyling(false)

    const files = Array.from(event.dataTransfer.files)
    if (files.length === 0) return

    this.addFiles(files)
  }
}
